/* PropertiesPanel — Right sidebar for editing selected node's config
 *
 * Smart field rendering:
 * - stage_change: Pipeline → From Stage → To Stage (stages filtered by pipeline)
 * - if_else: Field (from field_definitions) → Operator (adapts to type) → Value (adapts to type)
 * - create_task: Assign To (users + "File Assignee") + reminder interval
 * - Fallback: generic JSON Schema → form renderer
 */
import { useCallback, useMemo } from 'react';
import type { Node } from '@xyflow/react';
import css from '../AutomationEditor.module.css';
import {
  useLookupCaseTypes,
  useLookupUsers,
  useLookupFieldDefs,
  useLookupConnectorInstances,
} from '../../../hooks/useAutomations';
import type {
  AutomationNodeDef,
  LookupCaseType,
  LookupUser,
  LookupFieldDef,
  LookupConnectorInstance,
} from '../../../types/automations';
import DatePicker from '../../../components/ui/DatePicker/DatePicker';
import NodeIcon from './nodes/NodeIcon';

interface Props {
  node: Node | null;
  nodeDefMap: Record<string, AutomationNodeDef>;
  onUpdateConfig: (nodeId: string, config: Record<string, unknown>) => void;
  onUpdateLabel: (nodeId: string, label: string) => void;
  onDeleteNode: (nodeId: string) => void;
}

export default function PropertiesPanel({
  node,
  nodeDefMap,
  onUpdateConfig,
  onUpdateLabel,
  onDeleteNode,
}: Props) {
  const { data: caseTypes = [] } = useLookupCaseTypes();
  const { data: users = [] } = useLookupUsers();
  const { data: fieldDefs = [] } = useLookupFieldDefs();
  const { data: connectorInstances = [] } = useLookupConnectorInstances();

  if (!node) {
    return (
      <div className={css.propertiesPanel}>
        <div className={css.propertiesPanelEmpty}>
          <span style={{ fontSize: 24 }}>🖱️</span>
          <span>Select a node to edit its properties</span>
        </div>
      </div>
    );
  }

  const defKey = node.data.node_def_key as string;
  const def = nodeDefMap[defKey];
  const config = (node.data.config as Record<string, unknown>) || {};

  return (
    <div className={css.propertiesPanel}>
      <div className={css.propertiesHeader}>
        <div className={css.nodeIcon} style={{ background: (node.data.color as string) || '#888' }}>
          <NodeIcon icon={(node.data.icon as string) || '•'} />
        </div>
        <div className={css.propertiesTitle}>{def?.name || defKey}</div>
      </div>

      <div className={css.propertiesBody}>
        {/* Label — always first */}
        <LabelField
          nodeId={node.id}
          label={(node.data.label as string) || ''}
          onUpdateLabel={onUpdateLabel}
        />
        {/* Smart forms per node type */}
        {defKey === 'stage_change' && (
          <StageChangeFields
            nodeId={node.id}
            config={config}
            caseTypes={caseTypes}
            onUpdate={onUpdateConfig}
          />
        )}
        {defKey === 'field_change' && (
          <FieldChangeFields
            nodeId={node.id}
            config={config}
            fieldDefs={fieldDefs}
            onUpdate={onUpdateConfig}
          />
        )}
        {defKey === 'case_created' && (
          <CaseCreatedFields
            nodeId={node.id}
            config={config}
            caseTypes={caseTypes}
            onUpdate={onUpdateConfig}
          />
        )}
        {defKey === 'task_completed' && (
          <TaskCompletedFields
            nodeId={node.id}
            config={config}
            caseTypes={caseTypes}
            onUpdate={onUpdateConfig}
          />
        )}
        {defKey === 'if_else' && (
          <IfElseFields
            nodeId={node.id}
            config={config}
            fieldDefs={fieldDefs}
            onUpdate={onUpdateConfig}
          />
        )}
        {defKey === 'create_task' && (
          <CreateTaskFields
            nodeId={node.id}
            config={config}
            users={users}
            onUpdate={onUpdateConfig}
          />
        )}
        {defKey === 'change_stage' && (
          <ChangeStageFields
            nodeId={node.id}
            config={config}
            caseTypes={caseTypes}
            onUpdate={onUpdateConfig}
          />
        )}
        {defKey === 'send_email' && (
          <SendEmailFields nodeId={node.id} config={config} onUpdate={onUpdateConfig} />
        )}
        {defKey === 'send_notification' && (
          <SendNotificationFields
            nodeId={node.id}
            config={config}
            users={users}
            onUpdate={onUpdateConfig}
          />
        )}{' '}
        {defKey === 'assign_to_user' && (
          <AssignToUserFields
            nodeId={node.id}
            config={config}
            users={users}
            onUpdate={onUpdateConfig}
          />
        )}
        {defKey === 'delay' && (
          <DelayFields nodeId={node.id} config={config} onUpdate={onUpdateConfig} />
        )}
        {defKey === 'invoke_connector' && (
          <InvokeConnectorFields
            nodeId={node.id}
            config={config}
            connectorInstances={connectorInstances}
            onUpdate={onUpdateConfig}
          />
        )}
        {defKey === 'end' && (
          <div style={{ color: 'var(--gray-400)', fontSize: 'var(--font-size-sm)' }}>
            No configurable properties — this marks the end of a workflow branch.
          </div>
        )}
        {/* Delete node button */}
        <button
          className={css.deleteNodeBtn}
          onClick={() => {
            if (
              window.confirm(
                `Remove "${(node.data.label as string) || def?.name || defKey}" from the canvas?`,
              )
            ) {
              onDeleteNode(node.id);
            }
          }}
        >
          🗑️ Remove Node
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
 *  Label (always shown)
 * ═══════════════════════════════════════════════ */
function LabelField({
  nodeId,
  label,
  onUpdateLabel,
}: {
  nodeId: string;
  label: string;
  onUpdateLabel: (id: string, l: string) => void;
}) {
  return (
    <div className={css.formGroup}>
      <label className={css.formLabel}>Label</label>
      <input
        className={css.formInput}
        value={label}
        onChange={(e) => onUpdateLabel(nodeId, e.target.value)}
        placeholder="Node label"
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════
 *  Stage Change — Pipeline ➜ From Stage ➜ To Stage
 * ═══════════════════════════════════════════════ */
function StageChangeFields({
  nodeId,
  config,
  caseTypes,
  onUpdate,
}: {
  nodeId: string;
  config: Record<string, unknown>;
  caseTypes: LookupCaseType[];
  onUpdate: (id: string, c: Record<string, unknown>) => void;
}) {
  const selectedCaseTypeId = config.case_type_id != null ? String(config.case_type_id) : '';
  const selectedCaseType = caseTypes.find((ct) => String(ct.id) === selectedCaseTypeId);
  const stages = selectedCaseType?.stages || [];

  const set = useCallback(
    (key: string, val: unknown) => {
      const next = { ...config, [key]: val };
      // Clear stage selections when pipeline changes
      if (key === 'case_type_id') {
        next.from_stage_id = '';
        next.to_stage_id = '';
      }
      onUpdate(nodeId, next);
    },
    [nodeId, config, onUpdate],
  );

  return (
    <>
      <div className={css.formGroup}>
        <label className={css.formLabel}>Pipeline *</label>
        <select
          className={css.formSelect}
          value={selectedCaseTypeId}
          onChange={(e) => set('case_type_id', e.target.value ? Number(e.target.value) : '')}
        >
          <option value="">— Select pipeline —</option>
          {caseTypes.map((ct) => (
            <option key={ct.id} value={ct.id}>
              {ct.display_name}
            </option>
          ))}
        </select>
      </div>

      <div className={css.formGroup}>
        <label className={css.formLabel}>From Stage</label>
        <select
          className={css.formSelect}
          value={config.from_stage_id != null ? String(config.from_stage_id) : ''}
          onChange={(e) => set('from_stage_id', e.target.value ? Number(e.target.value) : '')}
          disabled={!selectedCaseTypeId}
        >
          <option value="">— Any stage —</option>
          {stages.map((s) => (
            <option key={s.stage_id} value={s.stage_id}>
              {s.stage_name}
            </option>
          ))}
        </select>
        <span className={css.formHint}>Leave blank to trigger on any source stage</span>
      </div>

      <div className={css.formGroup}>
        <label className={css.formLabel}>To Stage *</label>
        <select
          className={css.formSelect}
          value={config.to_stage_id != null ? String(config.to_stage_id) : ''}
          onChange={(e) => set('to_stage_id', e.target.value ? Number(e.target.value) : '')}
          disabled={!selectedCaseTypeId}
        >
          <option value="">— Select stage —</option>
          {stages.map((s) => (
            <option key={s.stage_id} value={s.stage_id}>
              {s.stage_name}
            </option>
          ))}
        </select>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════ *  Field Change — Field selector (any change to that field fires the trigger)
 * ═════════════════════════════════════════════ */
function FieldChangeFields({
  nodeId,
  config,
  fieldDefs,
  onUpdate,
}: {
  nodeId: string;
  config: Record<string, unknown>;
  fieldDefs: LookupFieldDef[];
  onUpdate: (id: string, c: Record<string, unknown>) => void;
}) {
  const grouped = useMemo(() => {
    const groups: Record<string, LookupFieldDef[]> = {};
    for (const fd of fieldDefs) {
      const cat = fd.category_name || 'Other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(fd);
    }
    return groups;
  }, [fieldDefs]);

  return (
    <div className={css.formGroup}>
      <label className={css.formLabel}>Field *</label>
      <select
        className={css.formSelect}
        value={(config.field_key as string) ?? ''}
        onChange={(e) => onUpdate(nodeId, { ...config, field_key: e.target.value || '' })}
      >
        <option value="">— Select field —</option>
        {Object.entries(grouped).map(([cat, fields]) => (
          <optgroup key={cat} label={cat}>
            {fields.map((f) => (
              <option key={f.field_key} value={f.field_key}>
                {f.field_name}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      <span className={css.formHint}>
        Triggers on any change to this field, regardless of the new value
      </span>
    </div>
  );
}

/* ═════════════════════════════════════════════ *  If / Else — Field ➜ Operator ➜ Value
 * ═══════════════════════════════════════════════ */

const OPERATORS_BY_TYPE: Record<string, { value: string; label: string }[]> = {
  string: [
    { value: 'eq', label: 'Equals' },
    { value: 'neq', label: 'Not equals' },
    { value: 'contains', label: 'Contains' },
    { value: 'not_contains', label: 'Does not contain' },
    { value: 'starts_with', label: 'Starts with' },
    { value: 'ends_with', label: 'Ends with' },
    { value: 'empty', label: 'Is empty' },
    { value: 'not_empty', label: 'Is not empty' },
  ],
  text: [
    { value: 'eq', label: 'Equals' },
    { value: 'neq', label: 'Not equals' },
    { value: 'contains', label: 'Contains' },
    { value: 'not_contains', label: 'Does not contain' },
    { value: 'empty', label: 'Is empty' },
    { value: 'not_empty', label: 'Is not empty' },
  ],
  dropdown: [
    { value: 'eq', label: 'Equals' },
    { value: 'neq', label: 'Not equals' },
    { value: 'in', label: 'Is one of' },
    { value: 'empty', label: 'Is empty' },
    { value: 'not_empty', label: 'Is not empty' },
  ],
  date: [
    { value: 'eq', label: 'Equals' },
    { value: 'neq', label: 'Not equals' },
    { value: 'gt', label: 'After' },
    { value: 'lt', label: 'Before' },
    { value: 'gte', label: 'On or after' },
    { value: 'lte', label: 'On or before' },
    { value: 'empty', label: 'Is empty' },
    { value: 'not_empty', label: 'Is not empty' },
  ],
  datetime: [
    { value: 'eq', label: 'Equals' },
    { value: 'neq', label: 'Not equals' },
    { value: 'gt', label: 'After' },
    { value: 'lt', label: 'Before' },
    { value: 'empty', label: 'Is empty' },
    { value: 'not_empty', label: 'Is not empty' },
  ],
  int: [
    { value: 'eq', label: 'Equals' },
    { value: 'neq', label: 'Not equals' },
    { value: 'gt', label: 'Greater than' },
    { value: 'lt', label: 'Less than' },
    { value: 'gte', label: '>=' },
    { value: 'lte', label: '<=' },
    { value: 'empty', label: 'Is empty' },
    { value: 'not_empty', label: 'Is not empty' },
  ],
  float: [
    { value: 'eq', label: 'Equals' },
    { value: 'neq', label: 'Not equals' },
    { value: 'gt', label: 'Greater than' },
    { value: 'lt', label: 'Less than' },
    { value: 'gte', label: '>=' },
    { value: 'lte', label: '<=' },
    { value: 'empty', label: 'Is empty' },
    { value: 'not_empty', label: 'Is not empty' },
  ],
  number: [
    { value: 'eq', label: 'Equals' },
    { value: 'neq', label: 'Not equals' },
    { value: 'gt', label: 'Greater than' },
    { value: 'lt', label: 'Less than' },
    { value: 'gte', label: '>=' },
    { value: 'lte', label: '<=' },
    { value: 'empty', label: 'Is empty' },
    { value: 'not_empty', label: 'Is not empty' },
  ],
  boolean: [
    { value: 'eq', label: 'Equals' },
    { value: 'neq', label: 'Not equals' },
  ],
  select: [
    { value: 'eq', label: 'Equals' },
    { value: 'neq', label: 'Not equals' },
    { value: 'in', label: 'Is one of' },
    { value: 'empty', label: 'Is empty' },
    { value: 'not_empty', label: 'Is not empty' },
  ],
  multiple: [
    { value: 'eq', label: 'Equals' },
    { value: 'neq', label: 'Not equals' },
    { value: 'in', label: 'Is one of' },
    { value: 'empty', label: 'Is empty' },
    { value: 'not_empty', label: 'Is not empty' },
  ],
};

function IfElseFields({
  nodeId,
  config,
  fieldDefs,
  onUpdate,
}: {
  nodeId: string;
  config: Record<string, unknown>;
  fieldDefs: LookupFieldDef[];
  onUpdate: (id: string, c: Record<string, unknown>) => void;
}) {
  const selectedFieldKey = (config.field as string) || '';
  const selectedField = fieldDefs.find((f) => f.field_key === selectedFieldKey);
  const fieldType = selectedField?.field_type || 'string';
  const operators = OPERATORS_BY_TYPE[fieldType] || OPERATORS_BY_TYPE.string;
  const selectedOp = (config.operator as string) || '';
  const isNoValueOp = selectedOp === 'empty' || selectedOp === 'not_empty';

  // Group field defs by category
  const grouped = useMemo(() => {
    const groups: Record<string, LookupFieldDef[]> = {};
    for (const fd of fieldDefs) {
      const cat = fd.category_name || 'Other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(fd);
    }
    return groups;
  }, [fieldDefs]);

  const set = useCallback(
    (key: string, val: unknown) => {
      const next = { ...config, [key]: val };
      // Reset downstream when field changes
      if (key === 'field') {
        next.operator = '';
        next.value = '';
      }
      onUpdate(nodeId, next);
    },
    [nodeId, config, onUpdate],
  );

  return (
    <>
      {/* Field selection */}
      <div className={css.formGroup}>
        <label className={css.formLabel}>Field *</label>
        <select
          className={css.formSelect}
          value={selectedFieldKey}
          onChange={(e) => set('field', e.target.value)}
        >
          <option value="">— Select field —</option>
          {Object.entries(grouped).map(([cat, fields]) => (
            <optgroup key={cat} label={cat}>
              {fields.map((f) => (
                <option key={f.field_key} value={f.field_key}>
                  {f.field_name}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      {/* Operator */}
      <div className={css.formGroup}>
        <label className={css.formLabel}>Operator *</label>
        <select
          className={css.formSelect}
          value={selectedOp}
          onChange={(e) => set('operator', e.target.value)}
          disabled={!selectedFieldKey}
        >
          <option value="">— Select operator —</option>
          {operators.map((op) => (
            <option key={op.value} value={op.value}>
              {op.label}
            </option>
          ))}
        </select>
      </div>

      {/* Value — adapts to field type */}
      {!isNoValueOp && (
        <DynamicValueField
          config={config}
          fieldType={fieldType}
          fieldDef={selectedField}
          onChange={(val) => set('value', val)}
          disabled={!selectedFieldKey || !selectedOp}
        />
      )}
    </>
  );
}

/* Dynamic value input that adapts to field type */
function DynamicValueField({
  config,
  fieldType,
  fieldDef,
  onChange,
  disabled,
}: {
  config: Record<string, unknown>;
  fieldType: string;
  fieldDef?: LookupFieldDef;
  onChange: (v: unknown) => void;
  disabled: boolean;
}) {
  const value = config.value;

  // Dropdown / select / multiple → show dropdown of same options
  const isDropdownType =
    fieldType === 'dropdown' || fieldType === 'select' || fieldType === 'multiple';
  // Options from field_options table, or fallback to validation_rules.options
  const dropdownOptions = useMemo(() => {
    if (fieldDef?.options && fieldDef.options.length > 0) return fieldDef.options;
    const vrOpts = (fieldDef?.validation_rules as Record<string, unknown> | null)?.options;
    if (Array.isArray(vrOpts) && vrOpts.length > 0) {
      return vrOpts.map((o: unknown) =>
        typeof o === 'string' ? { value: o, label: o } : (o as { value: string; label: string }),
      );
    }
    return [];
  }, [fieldDef]);

  if (isDropdownType && dropdownOptions.length > 0) {
    return (
      <div className={css.formGroup}>
        <label className={css.formLabel}>Value *</label>
        <select
          className={css.formSelect}
          disabled={disabled}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">— Select value —</option>
          {dropdownOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  // Date field → date input
  if (fieldType === 'date' || fieldType === 'datetime') {
    return (
      <div className={css.formGroup}>
        <label className={css.formLabel}>Value *</label>
        <DatePicker
          value={(value as string) ?? ''}
          onChange={onChange}
          disabled={disabled}
          className={css.formInput}
        />
      </div>
    );
  }

  // Boolean field → dropdown true/false
  if (fieldType === 'boolean') {
    return (
      <div className={css.formGroup}>
        <label className={css.formLabel}>Value *</label>
        <select
          className={css.formSelect}
          disabled={disabled}
          value={value != null ? String(value) : ''}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">— Select —</option>
          <option value="true">True</option>
          <option value="false">False</option>
        </select>
      </div>
    );
  }

  // Number field → number input
  if (fieldType === 'int' || fieldType === 'float' || fieldType === 'number') {
    return (
      <div className={css.formGroup}>
        <label className={css.formLabel}>Value *</label>
        <input
          className={css.formInput}
          type="number"
          disabled={disabled}
          value={value != null ? String(value) : ''}
          onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
        />
      </div>
    );
  }

  // Default: text input
  return (
    <div className={css.formGroup}>
      <label className={css.formLabel}>Value *</label>
      <input
        className={css.formInput}
        type="text"
        disabled={disabled}
        value={(value as string) ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter value"
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════
 *  Create Task — Description, Assign To, Deadline (relative), Reminders
 * ═══════════════════════════════════════════════ */

function CreateTaskFields({
  nodeId,
  config,
  users,
  onUpdate,
}: {
  nodeId: string;
  config: Record<string, unknown>;
  users: LookupUser[];
  onUpdate: (id: string, c: Record<string, unknown>) => void;
}) {
  const set = useCallback(
    (key: string, val: unknown) => onUpdate(nodeId, { ...config, [key]: val }),
    [nodeId, config, onUpdate],
  );

  const allowReminders = !!config.allow_reminders;
  const deadlineDays = config.deadline_days != null ? Number(config.deadline_days) : '';
  const skipWeekends = !!config.skip_weekends;
  const reminderDaysBefore =
    config.reminder_days_before != null ? Number(config.reminder_days_before) : '';

  return (
    <>
      <div className={css.formGroup}>
        <label className={css.formLabel}>Task Description *</label>
        <textarea
          className={css.formTextarea}
          value={(config.description as string) ?? ''}
          onChange={(e) => set('description', e.target.value)}
          placeholder="Describe the task..."
        />
      </div>

      <div className={css.formGroup}>
        <label className={css.formLabel}>Assign To *</label>
        <select
          className={css.formSelect}
          value={(config.assign_to as string) ?? ''}
          onChange={(e) => set('assign_to', e.target.value)}
        >
          <option value="">— Select assignee —</option>
          <option value="__file_assignee__">📁 File Assignee (assigned user on case)</option>
          {users.map((u) => (
            <option key={u.username} value={u.username}>
              {u.full_name ? `${u.full_name} (${u.username})` : u.username}
            </option>
          ))}
        </select>
        <span className={css.formHint}>
          &quot;File Assignee&quot; assigns to whoever is the assigned user on the case
        </span>
      </div>

      <div className={css.formGroup}>
        <label className={css.formLabel}>Deadline (days from trigger date)</label>
        <input
          className={css.formInput}
          type="number"
          min={1}
          value={deadlineDays}
          onChange={(e) => set('deadline_days', e.target.value ? Number(e.target.value) : '')}
          placeholder="e.g. 3"
        />
        <span className={css.formHint} title="Deadline time is fixed at 5:00 PM">
          ⏰ Deadline will be set to 5:00 PM on the calculated date
        </span>
      </div>

      <div className={css.formGroup}>
        <div className={css.formCheckbox}>
          <input
            type="checkbox"
            id={`${nodeId}-skip_weekends`}
            checked={skipWeekends}
            onChange={(e) => set('skip_weekends', e.target.checked)}
          />
          <label
            htmlFor={`${nodeId}-skip_weekends`}
            title="When enabled, only Monday–Friday are counted"
          >
            Skip weekends (business days only)
          </label>
        </div>
      </div>

      <div className={css.formGroup}>
        <div className={css.formCheckbox}>
          <input
            type="checkbox"
            id={`${nodeId}-allow_reminders`}
            checked={allowReminders}
            onChange={(e) => {
              if (!e.target.checked) {
                onUpdate(nodeId, {
                  ...config,
                  allow_reminders: false,
                  reminder_days_before: null,
                });
              } else {
                set('allow_reminders', true);
              }
            }}
          />
          <label htmlFor={`${nodeId}-allow_reminders`}>Enable Reminder</label>
        </div>
      </div>

      {allowReminders && (
        <div className={css.formGroup}>
          <label className={css.formLabel}>Remind X days before deadline</label>
          <input
            className={css.formInput}
            type="number"
            min={1}
            value={reminderDaysBefore}
            onChange={(e) =>
              set('reminder_days_before', e.target.value ? Number(e.target.value) : '')
            }
            placeholder="e.g. 1"
          />
          <span
            className={css.formHint}
            title="Reminder time is fixed at 9:00 AM. Weekend-skipping applies if enabled above."
          >
            ⏰ Reminder will be sent at 9:00 AM — respects the skip-weekends setting
          </span>
        </div>
      )}
    </>
  );
}

/* ═══════════════════════════════════════════════
 *  Case Created — Pipeline filter (optional)
 * ═══════════════════════════════════════════════ */
function CaseCreatedFields({
  nodeId,
  config,
  caseTypes,
  onUpdate,
}: {
  nodeId: string;
  config: Record<string, unknown>;
  caseTypes: LookupCaseType[];
  onUpdate: (id: string, c: Record<string, unknown>) => void;
}) {
  return (
    <div className={css.formGroup}>
      <label className={css.formLabel}>Pipeline</label>
      <select
        className={css.formSelect}
        value={config.case_type_id != null ? String(config.case_type_id) : ''}
        onChange={(e) =>
          onUpdate(nodeId, {
            ...config,
            case_type_id: e.target.value ? Number(e.target.value) : null,
          })
        }
      >
        <option value="">— Any pipeline —</option>
        {caseTypes.map((ct) => (
          <option key={ct.id} value={ct.id}>
            {ct.display_name}
          </option>
        ))}
      </select>
      <span className={css.formHint}>Leave blank to trigger on all new cases</span>
    </div>
  );
}

/* ═══════════════════════════════════════════════
 *  Task Completed — Pipeline filter (optional)
 * ═══════════════════════════════════════════════ */
function TaskCompletedFields({
  nodeId,
  config,
  caseTypes,
  onUpdate,
}: {
  nodeId: string;
  config: Record<string, unknown>;
  caseTypes: LookupCaseType[];
  onUpdate: (id: string, c: Record<string, unknown>) => void;
}) {
  return (
    <div className={css.formGroup}>
      <label className={css.formLabel}>Pipeline</label>
      <select
        className={css.formSelect}
        value={config.case_type_id != null ? String(config.case_type_id) : ''}
        onChange={(e) =>
          onUpdate(nodeId, {
            ...config,
            case_type_id: e.target.value ? Number(e.target.value) : null,
          })
        }
      >
        <option value="">— Any pipeline —</option>
        {caseTypes.map((ct) => (
          <option key={ct.id} value={ct.id}>
            {ct.display_name}
          </option>
        ))}
      </select>
      <span className={css.formHint}>Only trigger for tasks on cases in this pipeline</span>
    </div>
  );
}

/* ═══════════════════════════════════════════════
 *  Change Stage — Target Stage selector
 * ═══════════════════════════════════════════════ */
function ChangeStageFields({
  nodeId,
  config,
  caseTypes,
  onUpdate,
}: {
  nodeId: string;
  config: Record<string, unknown>;
  caseTypes: LookupCaseType[];
  onUpdate: (id: string, c: Record<string, unknown>) => void;
}) {
  const selectedCaseTypeId = config.case_type_id != null ? String(config.case_type_id) : '';
  const selectedCaseType = caseTypes.find((ct) => String(ct.id) === selectedCaseTypeId);
  const stages = selectedCaseType?.stages || [];

  const set = useCallback(
    (key: string, val: unknown) => {
      const next = { ...config, [key]: val };
      if (key === 'case_type_id') {
        next.target_stage_id = '';
      }
      onUpdate(nodeId, next);
    },
    [nodeId, config, onUpdate],
  );

  return (
    <>
      <div className={css.formGroup}>
        <label className={css.formLabel}>Pipeline *</label>
        <select
          className={css.formSelect}
          value={selectedCaseTypeId}
          onChange={(e) => set('case_type_id', e.target.value ? Number(e.target.value) : '')}
        >
          <option value="">— Select pipeline —</option>
          {caseTypes.map((ct) => (
            <option key={ct.id} value={ct.id}>
              {ct.display_name}
            </option>
          ))}
        </select>
        <span className={css.formHint}>Select the pipeline to choose a target stage</span>
      </div>

      <div className={css.formGroup}>
        <label className={css.formLabel}>Target Stage *</label>
        <select
          className={css.formSelect}
          value={config.target_stage_id != null ? String(config.target_stage_id) : ''}
          onChange={(e) => set('target_stage_id', e.target.value ? Number(e.target.value) : '')}
          disabled={!selectedCaseTypeId}
        >
          <option value="">— Select stage —</option>
          {stages.map((s) => (
            <option key={s.stage_id} value={s.stage_id}>
              {s.stage_name}
            </option>
          ))}
        </select>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════
 *  Send Email — To, Subject, Body
 * ═══════════════════════════════════════════════ */
function SendEmailFields({
  nodeId,
  config,
  onUpdate,
}: {
  nodeId: string;
  config: Record<string, unknown>;
  onUpdate: (id: string, c: Record<string, unknown>) => void;
}) {
  const set = useCallback(
    (key: string, val: unknown) => onUpdate(nodeId, { ...config, [key]: val }),
    [nodeId, config, onUpdate],
  );

  return (
    <>
      <div className={css.formGroup}>
        <label className={css.formLabel}>To *</label>
        <input
          className={css.formInput}
          type="text"
          value={(config.to as string) ?? ''}
          onChange={(e) => set('to', e.target.value)}
          placeholder="e.g. {{assigned_user_email}}"
        />
        <span className={css.formHint}>Supports template variables</span>
      </div>

      <div className={css.formGroup}>
        <label className={css.formLabel}>Subject *</label>
        <input
          className={css.formInput}
          type="text"
          value={(config.subject as string) ?? ''}
          onChange={(e) => set('subject', e.target.value)}
          placeholder="e.g. Case {{case_ref}} update"
        />
      </div>

      <div className={css.formGroup}>
        <label className={css.formLabel}>Body (HTML) *</label>
        <textarea
          className={css.formTextarea}
          value={(config.body as string) ?? ''}
          onChange={(e) => set('body', e.target.value)}
          placeholder="Email body — supports template variables"
          rows={5}
        />
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════
 *  Send Notification — Recipient, Message
 * ═══════════════════════════════════════════════ */
function SendNotificationFields({
  nodeId,
  config,
  users,
  onUpdate,
}: {
  nodeId: string;
  config: Record<string, unknown>;
  users: LookupUser[];
  onUpdate: (id: string, c: Record<string, unknown>) => void;
}) {
  const set = useCallback(
    (key: string, val: unknown) => onUpdate(nodeId, { ...config, [key]: val }),
    [nodeId, config, onUpdate],
  );

  return (
    <>
      <div className={css.formGroup}>
        <label className={css.formLabel}>Recipient *</label>
        <select
          className={css.formSelect}
          value={(config.recipient as string) ?? ''}
          onChange={(e) => set('recipient', e.target.value)}
        >
          <option value="">— Select recipient —</option>
          <option value="case_owner">📁 Case Owner</option>
          <option value="specific_user">👤 Specific User</option>
        </select>
      </div>

      {config.recipient === 'specific_user' && (
        <div className={css.formGroup}>
          <label className={css.formLabel}>User *</label>
          <select
            className={css.formSelect}
            value={(config.specific_user_id as string) ?? ''}
            onChange={(e) => set('specific_user_id', e.target.value)}
          >
            <option value="">— Select user —</option>
            {users.map((u) => (
              <option key={u.username} value={u.username}>
                {u.full_name ? `${u.full_name} (${u.username})` : u.username}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className={css.formGroup}>
        <label className={css.formLabel}>Message *</label>
        <textarea
          className={css.formTextarea}
          value={(config.message as string) ?? ''}
          onChange={(e) => set('message', e.target.value)}
          placeholder="Notification message — supports template variables"
          rows={3}
        />
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════
 *  Delay — Duration + Unit
 * ═══════════════════════════════════════════════ */
function DelayFields({
  nodeId,
  config,
  onUpdate,
}: {
  nodeId: string;
  config: Record<string, unknown>;
  onUpdate: (id: string, c: Record<string, unknown>) => void;
}) {
  const set = useCallback(
    (key: string, val: unknown) => onUpdate(nodeId, { ...config, [key]: val }),
    [nodeId, config, onUpdate],
  );

  return (
    <>
      <div className={css.formGroup}>
        <label className={css.formLabel}>Duration *</label>
        <input
          className={css.formInput}
          type="number"
          min={1}
          value={config.duration != null ? String(config.duration) : ''}
          onChange={(e) => set('duration', e.target.value ? Number(e.target.value) : '')}
          placeholder="e.g. 2"
        />
      </div>

      <div className={css.formGroup}>
        <label className={css.formLabel}>Unit *</label>
        <select
          className={css.formSelect}
          value={(config.unit as string) ?? ''}
          onChange={(e) => set('unit', e.target.value)}
        >
          <option value="">— Select unit —</option>
          <option value="minutes">Minutes</option>
          <option value="hours">Hours</option>
          <option value="days">Days</option>
        </select>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════
 *  Assign To User — single user or weighted distribution
 * ═══════════════════════════════════════════════ */
type WeightedUser = { user_id: string; weight: number };

function AssignToUserFields({
  nodeId,
  config,
  users,
  onUpdate,
}: {
  nodeId: string;
  config: Record<string, unknown>;
  users: LookupUser[];
  onUpdate: (id: string, c: Record<string, unknown>) => void;
}) {
  const mode = (config.mode as string) ?? 'single';
  const selectedUserId = (config.user_id as string) ?? '';
  const weightedUsers: WeightedUser[] = Array.isArray(config.weighted_users)
    ? (config.weighted_users as WeightedUser[])
    : [];

  const set = useCallback(
    (key: string, val: unknown) => onUpdate(nodeId, { ...config, [key]: val }),
    [nodeId, config, onUpdate],
  );

  const handleModeChange = (newMode: string) => {
    // Reset mode-specific fields when switching
    const {
      mode: _m,
      user_id: _u,
      weighted_users: _w,
      ...rest
    } = config as Record<string, unknown>;
    onUpdate(nodeId, { ...rest, mode: newMode });
  };

  const addWeightedUser = () => {
    set('weighted_users', [...weightedUsers, { user_id: '', weight: 0 }]);
  };

  const removeWeightedUser = (index: number) => {
    set(
      'weighted_users',
      weightedUsers.filter((_, i) => i !== index),
    );
  };

  const updateWeightedUser = (
    index: number,
    field: 'user_id' | 'weight',
    value: string | number,
  ) => {
    set(
      'weighted_users',
      weightedUsers.map((u, i) => (i === index ? { ...u, [field]: value } : u)),
    );
  };

  const totalWeight = weightedUsers.reduce((sum, u) => sum + (u.weight || 0), 0);
  const weightValid = weightedUsers.length > 0 && totalWeight === 100;

  return (
    <>
      <div className={css.formGroup}>
        <label className={css.formLabel}>Assignment Mode *</label>
        <select
          className={css.formSelect}
          value={mode}
          onChange={(e) => handleModeChange(e.target.value)}
        >
          <option value="single">Single User — always assign to same user</option>
          <option value="weighted">Weighted — distribute across multiple users</option>
        </select>
      </div>

      {mode === 'single' && (
        <div className={css.formGroup}>
          <label className={css.formLabel}>Assign To *</label>
          <select
            className={css.formSelect}
            value={selectedUserId}
            onChange={(e) => set('user_id', e.target.value)}
          >
            <option value="">— Select a user —</option>
            {users.map((u) => (
              <option key={u.cognito_user_id} value={u.cognito_user_id}>
                {u.full_name ? `${u.full_name} (${u.username})` : u.username}
              </option>
            ))}
          </select>
        </div>
      )}

      {mode === 'weighted' && (
        <>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Users &amp; Weights</label>
            <span className={css.formHint}>
              Weights must total exactly 100%. Cases are distributed using a persistent
              greatest-deficit algorithm — distribution stays correct over time and deployments.
            </span>
          </div>

          {weightedUsers.map((wu, idx) => (
            <div
              key={idx}
              className={css.formGroup}
              style={{ display: 'flex', gap: 8, alignItems: 'center' }}
            >
              <select
                className={css.formSelect}
                style={{ flex: 1 }}
                value={wu.user_id}
                onChange={(e) => updateWeightedUser(idx, 'user_id', e.target.value)}
              >
                <option value="">— Select user —</option>
                {users.map((u) => (
                  <option key={u.cognito_user_id} value={u.cognito_user_id}>
                    {u.full_name ? `${u.full_name} (${u.username})` : u.username}
                  </option>
                ))}
              </select>
              <input
                className={css.formInput}
                type="number"
                min={1}
                max={100}
                style={{ width: 70 }}
                value={wu.weight || ''}
                onChange={(e) =>
                  updateWeightedUser(idx, 'weight', e.target.value ? Number(e.target.value) : 0)
                }
                placeholder="%"
              />
              <button
                type="button"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--red-500)',
                  fontSize: 18,
                  padding: '0 4px',
                }}
                title="Remove"
                onClick={() => removeWeightedUser(idx)}
              >
                ✕
              </button>
            </div>
          ))}

          <div
            className={css.formGroup}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <button type="button" className={css.addConditionBtn} onClick={addWeightedUser}>
              + Add User
            </button>
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                color: weightValid ? 'var(--green-600)' : 'var(--red-500)',
                fontWeight: 600,
              }}
            >
              Total: {totalWeight}% {weightValid ? '✓' : '(must equal 100%)'}
            </span>
          </div>
        </>
      )}
    </>
  );
}

/* ═══════════════════════════════════════════════
 *  Invoke Connector — Integration ➜ Action or HTTP Method + Path
 * ═══════════════════════════════════════════════ */
function InvokeConnectorFields({
  nodeId,
  config,
  connectorInstances,
  onUpdate,
}: {
  nodeId: string;
  config: Record<string, unknown>;
  connectorInstances: LookupConnectorInstance[];
  onUpdate: (id: string, c: Record<string, unknown>) => void;
}) {
  const set = useCallback(
    (key: string, val: unknown) => onUpdate(nodeId, { ...config, [key]: val }),
    [nodeId, config, onUpdate],
  );

  const actionValue = (config.action as string) ?? '';
  const showGenericFields = !actionValue;

  return (
    <>
      <div className={css.formGroup}>
        <label className={css.formLabel}>Integration *</label>
        <select
          className={css.formSelect}
          value={config.instance_id != null ? String(config.instance_id) : ''}
          onChange={(e) => set('instance_id', e.target.value ? Number(e.target.value) : '')}
        >
          <option value="">— Select integration —</option>
          {connectorInstances.map((ci) => (
            <option key={ci.id} value={ci.id}>
              {ci.template_name}: {ci.name}
              {ci.status !== 'active' ? ` (${ci.status})` : ''}
            </option>
          ))}
        </select>
        {connectorInstances.length === 0 && (
          <span className={css.formHint}>No active integrations configured</span>
        )}
      </div>

      <div className={css.formGroup}>
        <label className={css.formLabel}>Action</label>
        <input
          className={css.formInput}
          type="text"
          value={actionValue}
          onChange={(e) => set('action', e.target.value)}
          placeholder="e.g. createMatter"
        />
        <span className={css.formHint}>
          Named action on the connector. Leave blank to use a generic HTTP request.
        </span>
      </div>

      {showGenericFields && (
        <>
          <div className={css.formGroup}>
            <label className={css.formLabel}>HTTP Method</label>
            <select
              className={css.formSelect}
              value={(config.method as string) ?? ''}
              onChange={(e) => set('method', e.target.value)}
            >
              <option value="">— Select method —</option>
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="PATCH">PATCH</option>
              <option value="DELETE">DELETE</option>
            </select>
          </div>

          <div className={css.formGroup}>
            <label className={css.formLabel}>Path</label>
            <input
              className={css.formInput}
              type="text"
              value={(config.path as string) ?? ''}
              onChange={(e) => set('path', e.target.value)}
              placeholder="e.g. /api/v4/matters"
            />
            <span className={css.formHint}>API path relative to the connector&apos;s base URL</span>
          </div>
        </>
      )}

      <div className={css.formGroup}>
        <label className={css.formLabel}>Timeout (ms)</label>
        <input
          className={css.formInput}
          type="number"
          min={1000}
          max={60000}
          step={1000}
          value={config.timeout != null ? String(config.timeout) : ''}
          onChange={(e) => set('timeout', e.target.value ? Number(e.target.value) : '')}
          placeholder="e.g. 10000"
        />
        <span className={css.formHint}>
          Request timeout in milliseconds (1000–60000). Default: 10000.
        </span>
      </div>
    </>
  );
}
